import { Directive, ElementRef, EventEmitter, OnDestroy, Output, Renderer2, Input } from '@angular/core';
import { IMAGE_PLACEHOLDER_PATH } from '@ag-common-lib/public-api';

@Directive({
  // eslint-disable-next-line @angular-eslint/directive-selector
  selector: '[imageLoadingState]',
})
export class ImageLoadingStateDirective implements OnDestroy {
  @Output() imageLoadingState: EventEmitter<boolean> = new EventEmitter<boolean>();
  @Output() isImageLoadingIssuesChange: EventEmitter<boolean> = new EventEmitter<boolean>();

  private img: HTMLImageElement;
  private usePlaceholder: boolean;
  /** Last applied image URL; skips redundant src updates so the image is not reloaded. */
  private currentSrc: string | null = null;
  /** Cleanup fn for the `load` listener; prevents stacking handlers across src changes. */
  private loadUnlisten: (() => void) | null = null;
  /** Cleanup fn for the `error` listener; prevents stacking handlers across src changes. */
  private errorUnlisten: (() => void) | null = null;
  /** Newly uploaded derivatives can 404 for a short time. Retry before locking the placeholder. */
  private readonly maxLoadRetries = 8;
  private readonly retryDelayMs = 500;
  private retryAttempt = 0;
  private retryTimer: ReturnType<typeof setTimeout> | null = null;

  constructor(
    private el: ElementRef,
    private renderer: Renderer2,
  ) {
    this.img = this.el.nativeElement as HTMLImageElement;
    this.initListeners();
  }

  @Input()
  set src(data: { src?: string; usePlaceholder?: boolean }) {
    const newSrc = data?.src;
    this.usePlaceholder = data?.usePlaceholder;

    if (!newSrc && !this.usePlaceholder) {
      return;
    }

    const nextSrc = newSrc ?? IMAGE_PLACEHOLDER_PATH;
    if (this.currentSrc === nextSrc) {
      return;
    }

    this.clearRetry();
    this.retryAttempt = 0;
    this.currentSrc = nextSrc;
    this.img.classList.remove('image-with-loader__image--error');
    this.img.src = nextSrc;
    this.imageLoadingState.emit(true);
  }

  ngOnDestroy(): void {
    this.clearRetry();
    this.teardownListeners();
  }

  private initListeners(): void {
    this.teardownListeners();

    this.loadUnlisten = this.renderer.listen(this.img, 'load', () => {
      this.clearRetry();
      this.retryAttempt = 0;
      this.imageLoadingState.emit(false);
      this.isImageLoadingIssuesChange.emit(false);
      this.img.classList.remove('image-with-loader__image--error');
    });

    this.errorUnlisten = this.renderer.listen(this.img, 'error', () => {
      if (!this.usePlaceholder) {
        this.clearRetry();
        this.imageLoadingState.emit(false);
        this.isImageLoadingIssuesChange.emit(true);
        this.img.classList.add('image-with-loader__image--error');
        return;
      }

      if (this.currentSrc === IMAGE_PLACEHOLDER_PATH) {
        this.imageLoadingState.emit(false);
        return;
      }

      if (this.scheduleRetry()) {
        return;
      }

      this.imageLoadingState.emit(false);
      this.isImageLoadingIssuesChange.emit(true);
      this.currentSrc = IMAGE_PLACEHOLDER_PATH;
      this.img.classList.remove('image-with-loader__image--error');
      this.img.src = IMAGE_PLACEHOLDER_PATH;
    });
  }

  private scheduleRetry(): boolean {
    const src = this.currentSrc;
    if (!src || this.retryAttempt >= this.maxLoadRetries) {
      return false;
    }

    this.retryAttempt += 1;
    const attempt = this.retryAttempt;
    this.img.classList.add('image-with-loader__image--error');
    this.imageLoadingState.emit(true);
    this.clearRetry();
    this.retryTimer = setTimeout(() => {
      this.retryTimer = null;
      if (this.currentSrc !== src) {
        return;
      }

      this.img.src = this.withRetryToken(src, attempt);
    }, this.retryDelayMs);

    return true;
  }

  private withRetryToken(src: string, attempt: number): string {
    const separator = src.includes('?') ? '&' : '?';
    return `${src}${separator}retry=${attempt}`;
  }

  private clearRetry(): void {
    if (this.retryTimer === null) {
      return;
    }

    clearTimeout(this.retryTimer);
    this.retryTimer = null;
  }

  private teardownListeners(): void {
    this.loadUnlisten?.();
    this.errorUnlisten?.();
    this.loadUnlisten = null;
    this.errorUnlisten = null;
  }
}
